import { apiFetch } from '../../lib/api'
import type {
  BookExecuteRequest,
  BookPreviewRequest,
  BookReceiptItemLike,
  BookReceiptLike,
  BookServerAction,
} from './bookActionController'
import type { BookReview } from './bookReview'
import { mapBookActionItems, type BookActionWire } from './bookActionsPanel'
import { bookReviewQuery, type BookReviewScope } from './bookReviewQuery'

export type BookActionItemResponse = BookActionWire

export interface BookActionsResponse {
  review: BookReview
  actions: readonly BookActionItemResponse[]
  selected_proposal_ids: readonly string[]
  also_books: readonly string[]
  dependency_ids: readonly string[]
  affected_fund_ids: readonly string[]
  requires_confirmation: boolean
  boundary_reasons: readonly string[]
  deck_version: string
}

export interface BookPreviewResponse {
  group_id: string
  presented_proposal_ids: readonly string[]
  review_binding_value: string
  deck_version: string
  actions: readonly BookActionItemResponse[]
  selected_proposal_ids: readonly string[]
  requires_confirmation: boolean
}

export function previewBookActions(
  groupId: string,
  body: BookPreviewRequest,
  signal: AbortSignal
): Promise<BookPreviewResponse> {
  return apiFetch<BookPreviewResponse>(
    '/api/rk/decks/group/' + encodeURIComponent(groupId) + '/book-actions/preview',
    { method: 'POST', body: JSON.stringify(body), signal }
  )
}

export interface BookExecuteAccepted {
  request_id: string
  group_id: string
  proposal_ids: readonly string[]
  state: 'approved_pending' | string
}

export interface BookActionReceiptItem extends BookReceiptItemLike {
  state: 'applied' | 'pending' | 'approved_pending' | 'refused' | 'deferred' | 'conflicted' | string
}

export interface BookActionReceipt extends BookReceiptLike {
  request_id: string
  group_id: string
  state: 'applied' | 'pending' | 'approved_pending' | 'refused' | 'deferred' | 'conflicted' | string
  proposal_ids: readonly string[]
  receipts: readonly BookActionReceiptItem[]
  pending_event_ids: readonly string[]
  binding_digest: string
}

export function mapBookActions(
  response:
    | Pick<BookActionsResponse, 'actions' | 'selected_proposal_ids' | 'requires_confirmation'>
    | readonly BookActionItemResponse[]
): BookServerAction[] {
  // Legacy item-only callers get individual actions; a union is never
  // reconstructed from those items on the client.
  if (!('actions' in response)) return mapBookActionItems(response)
  const mapped = mapBookActionItems(response.actions)
  if (!response.requires_confirmation || !response.selected_proposal_ids.length) return mapped
  return [
    ...mapped,
    {
      id: 'book-all',
      label: 'Book all',
      kind: 'book_all',
      selected_proposal_ids: response.selected_proposal_ids,
      disabled: false,
    },
  ]
}

export function fetchBookReview(
  groupId: string,
  proposalIds: readonly string[],
  scope: BookReviewScope = {},
  background = false
): Promise<BookReview> {
  return apiFetch<BookReview>(bookReviewQuery(groupId, proposalIds, scope), { background })
}

export function fetchBookActions(
  groupId: string,
  proposalIds: readonly string[],
  scope: BookReviewScope = {},
  background = false
): Promise<BookActionsResponse> {
  const url = bookReviewQuery(groupId, proposalIds, scope).replace(
    '/book-review?',
    '/book-actions?'
  )
  return apiFetch<BookActionsResponse>(url, { background })
}

export function executeBookAction(
  groupId: string,
  body: BookExecuteRequest,
  idempotencyKey: string
): Promise<BookExecuteAccepted> {
  return apiFetch<BookExecuteAccepted>(
    '/api/rk/decks/group/' + encodeURIComponent(groupId) + '/book-actions/execute',
    {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(body),
    }
  )
}

export function fetchBookActionReceipt(
  groupId: string,
  requestId: string,
  background = true
): Promise<BookActionReceipt> {
  return apiFetch<BookActionReceipt>(
    '/api/rk/decks/group/' +
      encodeURIComponent(groupId) +
      '/book-actions/execute/' +
      encodeURIComponent(requestId),
    { background }
  )
}
